import { useState } from "react";
import { toast } from "sonner";
import { Lock, Sparkles, CheckCircle2, Clock, Loader2, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SPECIALIST_MATCH_FEATURES } from "@/lib/packages";
import { StyleMeter } from "@/components/StyleMeter";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const SAMPLE = [
  { name: "Elena R.", title: "Family SUV & Financing", style: { D: 4, I: 7, S: 8, C: 5 }, score: "90%" },
  { name: "David M.", title: "Truck & Pre-Owned", style: { D: 8, I: 4, S: 6, C: 5 }, score: "80%" },
];

export function SpecialistMatchLocked({ dealer, onRequested }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const requested = dealer?.addon_specialist_match === "requested";

  const request = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dealer/addons/specialist-match/request", { note });
      toast.success("Request sent — Vinconex will activate Specialist Match for you shortly");
      onRequested?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="specialist-match-locked" className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 tracing-top rounded-2xl border border-[#2A2E3D] bg-[#12141C] p-7">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-700/50 bg-amber-950/40 text-amber-400 text-[11px] font-mono-tech uppercase tracking-widest mb-4">
          <Lock className="h-3 w-3" /> Add-on · not in your basic package
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-[#0A8CF2]" /> Specialist Match
        </h2>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
          Turn every QR scan into a warm hand-off. Shoppers take a 60-second style check and get paired with the product
          specialist who sells the way they buy — and your managers decide who earns the leads.
        </p>
        <ul className="mt-5 space-y-2.5">
          {SPECIALIST_MATCH_FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
              <CheckCircle2 className="h-4 w-4 text-[#0A8CF2] mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>

        <div className="mt-7 rounded-xl border border-slate-800 bg-slate-950 p-5">
          {requested ? (
            <div className="flex items-start gap-3" data-testid="addon-requested-state">
              <Clock className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Request sent — awaiting Vinconex activation</div>
                <p className="text-xs text-slate-500 mt-1">
                  Requested {dealer?.addon_specialist_match_requested_at ? new Date(dealer.addon_specialist_match_requested_at).toLocaleDateString() : "recently"}.
                  You'll see the Specialists tools unlock here the moment it's switched on.
                </p>
              </div>
            </div>
          ) : isAdmin ? (
            <>
              <div className="font-semibold mb-2">Add Specialist Match to {dealer?.name || "your dealership"}</div>
              <Textarea
                data-testid="addon-request-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note for Vinconex — team size, go-live date, questions…"
                className="bg-[#0F111A] border-slate-800 focus:border-[#0A8CF2] text-slate-100 min-h-[70px]"
              />
              <Button onClick={request} disabled={busy} data-testid="request-addon-btn" className="w-full h-11 mt-3 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Request Specialist Match add-on <Sparkles className="ml-2 h-4 w-4" /></>}
              </Button>
              <p className="text-[11px] text-slate-500 mt-2">Vinconex reviews and activates the add-on; billing is handled separately.</p>
            </>
          ) : (
            <p className="text-sm text-slate-400" data-testid="addon-manager-hint">
              Ask your dealership admin to request the Specialist Match add-on from this page.
            </p>
          )}
          {user?.platform_owner && (
            <p className="text-[11px] text-[#7FC3FF] mt-3 flex items-center gap-1.5" data-testid="addon-owner-hint">
              <Building2 className="h-3 w-3" /> CraveBeast LLC (platform owner) activates this add-on from the Platform page.
            </p>
          )}
        </div>
      </div>

      <div className="lg:col-span-2 relative" aria-hidden="true">
        <div className="absolute inset-0 z-10 rounded-2xl bg-gradient-to-b from-transparent via-[#0A0B0E]/40 to-[#0A0B0E]" />
        <div className="space-y-3 opacity-70 select-none pointer-events-none">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech">Preview</p>
          {SAMPLE.map((s) => (
            <div key={s.name} className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-4">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-slate-800 border-2 border-[#0A8CF2]" />
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-bold">{s.name}</div>
                  <div className="text-xs text-slate-400">{s.title}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1">Style</div>
                  <StyleMeter style={s.style} />
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1">CRM score</div>
                  <div className="font-heading text-2xl font-black leading-none">{s.score}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
